import axios from 'axios';
import config from './../config';
import Swal from 'sweetalert2';
import { setAuth } from './reducers/user';
import { store } from './store';
import { isEqual } from 'lodash';

export const api = axios.create({
    baseURL: config.API_ROOT,
});
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Kyiv';

export const apiNoBase = axios.create({});

let failedQueue = [];
let isRefreshing = false;

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

api.interceptors.request.use(
    function (config) {
        if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${localStorage.accessToken}`;
        config.headers.TimeZone = timezone;
        return config;
    },
    function (error) {
        return Promise.reject(error);
    },
);

apiNoBase.interceptors.request.use(
    function (config) {
        if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${localStorage.accessToken}`;
        return config;
    },
    function (error) {
        return Promise.reject(error);
    },
);

api.interceptors.response.use(
    (response) => {
        if (response.data?.success && response.data?.data) {
            response.data = response.data.data;
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        if (error.response.status === 403 && !originalRequest.retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers.Authorization = token;
                        return api(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            originalRequest.retry = true;
            isRefreshing = true;

            try {
                const { data } = await api.get('auth/refresh-token', {
                    headers: { RefreshToken: localStorage.refreshToken },
                });

                const accessToken = `Bearer ${data.access_token}`;

                localStorage.setItem('accessToken', accessToken);
                localStorage.setItem('refreshToken', data.refresh_token);

                originalRequest.headers.Authorization = accessToken;

                processQueue(null, accessToken);

                return await api(originalRequest);
            } catch (err) {
                processQueue(err, null);
                store.dispatch(setAuth(false));
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    },
);

export const request = async ({ url, attributes = null, method = 'get', success = () => '', fail = () => '', finish = () => '', params = null }) => {
    try {
        let response = {};
        if (isEqual(method, 'patch')) {
            response = await api.patch(url, attributes);
        } else if (isEqual(method, 'put')) {
            response = await api.put(url, attributes, { params });
        } else if (isEqual(method, 'delete')) {
            response = await api.delete(url, { data: attributes });
        } else if (isEqual(method, 'post')) {
            response = await api.post(url, attributes, { params });
        } else {
            response = await api.get(url, { params });
        }

        success(response);

        finish(true);
        return response;
    } catch (e) {
        fail(e);
        finish(false);
    }
};

export const downloadFile = async ({
    url,
    success = () => {},
    fail = () => {},
    method = 'get',
    fileName = 'file',
    mimeType,
    fileNameWithMimeType = '',
}) => {
    try {
        const res = await request({ url, success, fail, method });

        const newUrl = window.URL.createObjectURL(new Blob([res.data]));
        download(newUrl, fileNameWithMimeType ? fileNameWithMimeType : `${fileName}.${mimeType}`);
        success(newUrl, res.data);
    } catch (e) {
        handleError(e);
    }
};

export const handleError = (error) => {
    if (error?.response?.data?.errors) {
        Swal.fire({
            icon: 'error',
            title: 'Error List',
            html: error.response.data.errors.reduce(
                (acc, err, index) => `${acc} <p style="margin: 0 0 10px 20px; display: flex">${index + 1}) ${err.errorMsg}</p>`,
                '',
            ),
        });
    } else {
        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error?.response?.data?.error || error.message,
        });
    }
};

const download = (url, name) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', name);
    link.click();
};
